import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import Button from './Button';
import Dialog, { DialogProps } from './Dialog';
import DialogActions from './DialogActions';
import DialogContent from './DialogContent';
import DialogTitle from './DialogTitle';
import TextField from './TextField';
import Typography from './Typography';

const messages = defineMessages({
  title: {
    id: 'components.v2.DialogMessageTemplateVersionDescription.title',
    defaultMessage: 'Name this version and save',
  },
  content: {
    id: 'components.v2.DialogMessageTemplateVersionDescription.content',
    defaultMessage: 'What did you change?',
  },
  namePlaceholder: {
    id: 'components.v2.DialogMessageTemplateVersionDescription.namePlaceholder',
    defaultMessage: 'A brief description of what you did, Zendesk ticket number, etc.',
  },
  actionCancel: {
    id: 'components.v2.DialogMessageTemplateVersionDescription.actionCancel',
    defaultMessage: 'Continue editing',
  },
  actionSubmit: {
    id: 'components.v2.DialogMessageTemplateVersionDescription.actionSubmit',
    defaultMessage: 'Save version',
  },
  errorLength: {
    id: 'components.v2.DialogMessageTemplateVersionDescription.errorLength',
    defaultMessage: 'The maximum length is 84 characters',
  },
});

export interface DialogMessageTemplateVersionDescriptionProps
  extends Omit<DialogProps, 'classes' | 'onClose' | 'onSubmit'> {
  onClose: (success?: boolean) => void;
  onSubmit: (name: string) => void;
}

const DialogMessageTemplateVersionDescription: React.FC<DialogMessageTemplateVersionDescriptionProps> = ({
  open,
  onClose,
  onSubmit,
  ...rest
}) => {
  const { formatMessage } = useIntl();
  const [name, setName] = useState('');

  useEffect(() => {
    if (!open) {
      setName('');
    }
  }, [open, setName]);

  const handleFieldMaxSize = (newValue: string) => {
    if (newValue && newValue.length > 84) {
      return newValue.substring(0, 84);
    }
    return newValue;
  };

  return (
    <Dialog open={open} fullWidth onClose={() => onClose()} {...rest}>
      <DialogTitle>{formatMessage(messages.title)}</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" paragraph>
          {formatMessage(messages.content)}
        </Typography>
        <TextField
          value={name}
          placeholder={formatMessage(messages.namePlaceholder)}
          onChange={(event) => setName(handleFieldMaxSize(event.target.value))}
          error={!!(name && name.length >= 84)}
          helperText={name && name.length >= 84 ? formatMessage(messages.errorLength) : false}
          fullWidth
          required
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={() => onClose()}>
          {formatMessage(messages.actionCancel)}
        </Button>
        <Button variant="contained" color="primary" onClick={() => onSubmit(name)} disabled={!name}>
          {formatMessage(messages.actionSubmit)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogMessageTemplateVersionDescription;
